import React, { useState, useRef, useEffect } from 'react';
import { TextField, Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormContainer from '../../components/FormContainer';
import { addReligion, updateReligion } from '../../utils/api';

const ReligionForm = ({ editReligion = null, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const isEditMode = !!editReligion;
  const onSuccessTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (onSuccessTimerRef.current != null) {
        clearTimeout(onSuccessTimerRef.current);
        onSuccessTimerRef.current = null;
      }
    },
    []
  );

  const validationSchema = Yup.object({
    religionName: Yup.string().required('Religion name is required').max(100, 'Religion name must be at most 100 characters'),
    description: Yup.string().required('Description is required').max(500, 'Description must be at most 500 characters'),
  });

  const formik = useFormik({
    initialValues: {
      religionName: editReligion ? editReligion.religionName : '',
      description: editReligion ? editReligion.description : '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      try {
        if (isEditMode) {
          await updateReligion(editReligion.religionId, values, {
            previousData: editReligion,
            identifier: editReligion.religionName || values.religionName
          });
          setSuccessMessage('Religion updated successfully!');
        } else {
          await addReligion(values, {
            identifier: values.religionName
          });
          setSuccessMessage('Religion added successfully!');
          resetForm();
        }

        if (onSuccess) {
          if (onSuccessTimerRef.current != null) clearTimeout(onSuccessTimerRef.current);
          onSuccessTimerRef.current = setTimeout(() => {
            onSuccessTimerRef.current = null;
            onSuccess();
          }, 1500);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'adding'} religion:`, error);
        setErrorMessage(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} religion. Please try again.`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  return (
    <FormContainer
      title={isEditMode ? "Edit Religion" : "Add Religion"}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={errorMessage}
      onCloseSnackbar={handleCloseSnackbar}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="religionName"
            name="religionName"
            label="Religion Name"
            value={formik.values.religionName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.religionName && Boolean(formik.errors.religionName)}
            helperText={formik.touched.religionName && formik.errors.religionName}
            margin="normal"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="description"
            name="description"
            label="Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            margin="normal"
            variant="outlined"
            multiline
            rows={4}
          />
        </Grid>
      </Grid>
    </FormContainer>
  );
};

export default ReligionForm; 